import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Presentation, FileQuestion, NotebookPen,
  FolderHeart, Sparkles, LogOut, Crown, Menu, X, ScanLine,
} from "lucide-react";

const links = [
  { to: "/app", label: "Табло", icon: LayoutDashboard, end: true },
  { to: "/app/presentation", label: "Презентации", icon: Presentation },
  { to: "/app/worksheet", label: "Работни листове", icon: FileQuestion },
  { to: "/app/lesson-plan", label: "Резюме на урока", icon: NotebookPen },
  { to: "/app/grading", label: "Проверка на тестове", icon: ScanLine },
  { to: "/app/my-resources", label: "Моите материали", icon: FolderHeart },
];

const planLabels = { free: "Безплатен", pro: "Pro", school: "Училище" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true));

  // Close the drawer automatically on small screens after navigating.
  const closeOnMobile = () => {
    if (window.innerWidth < 1024) setOpen(false);
  };

  useEffect(() => {
    const onResize = () => setOpen(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Top bar with toggle — always visible so the menu never blocks the view */}
      <header className="sticky top-0 z-40 flex items-center gap-3 h-16 px-4 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <button
          data-testid="menu-toggle-btn"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Затвори менюто" : "Отвори менюто"}
          className="p-2.5 rounded-2xl bg-sky-50 text-sky-600 hover:bg-sky-100 transition-colors"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
        <div className="flex items-center gap-2">
          <div className="bg-sky-500 text-white p-1.5 rounded-xl"><Sparkles size={16} /></div>
          <span className="font-heading font-black text-lg text-slate-900">УчителAI</span>
        </div>
        <div className="ml-auto text-xs font-semibold text-slate-400 hidden sm:block">
          {user?.name} · {planLabels[user?.plan] || "Безплатен"}
        </div>
      </header>

      {/* Backdrop for mobile overlay */}
      {open && (
        <div
          data-testid="menu-backdrop"
          onClick={() => setOpen(false)}
          className="fixed inset-0 top-16 bg-slate-900/20 z-30 lg:hidden"
        />
      )}

      {/* Sidebar drawer */}
      <aside
        className={`fixed top-16 left-0 bottom-0 w-64 bg-white border-r border-slate-100 flex flex-col z-30 transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={closeOnMobile}
              data-testid={`nav-${l.to.split("/").pop() || "dashboard"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl font-semibold text-sm transition-colors ${
                  isActive ? "bg-sky-50 text-sky-600" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <l.icon size={18} />
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3">
          <button
            data-testid="nav-pricing"
            onClick={() => { navigate("/app/pricing"); closeOnMobile(); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-semibold text-sm text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors mb-2"
          >
            <Crown size={18} /> Планове
          </button>
          <div className="px-4 py-3 rounded-2xl bg-slate-50">
            <div className="text-sm font-bold text-slate-900 truncate">{user?.name}</div>
            <div className="text-xs text-slate-400 mb-2">План: {planLabels[user?.plan] || "Безплатен"}</div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 text-xs text-slate-500 hover:text-red-500 transition-colors"
            >
              <LogOut size={14} /> Изход
            </button>
          </div>
        </div>
      </aside>

      {/* Main content shifts only on large screens when the drawer is open */}
      <main className={`p-6 md:p-10 transition-[margin] duration-300 ${open ? "lg:ml-64" : "ml-0"}`}>
        {children}
      </main>
    </div>
  );
}
